import { memo } from 'react';
import { MdContentCopy, MdAutorenew } from 'react-icons/md';
import { IconButton } from '@mui/material';
import cn from 'classnames';

import { OButton, OTooltip } from '@src/shared';
import { usePeersStore } from '@src/stores/peers.store';

import styles from './panelistInviteModal.module.scss';
import usePanelistInviteModal from './usePanelistInviteModal';

interface IPanelistInviteContentProps {
  handleOpenQr: () => void;
}

const PanelistInviteContent = ({ handleOpenQr }: IPanelistInviteContentProps) => {
  const isHostUser = usePeersStore((state) => state.isHostUser);
  const {
    meetingLink,
    invitesRemaining,
    invitesLimit,
    isGeneratingCoHost,
    isGeneratingSpeaker,
    generatedCoHostLink,
    generatedSpeakerLink,
    handleCopyLink,
    handleOpenMail,
    handleGenerateCoHostLink,
    handleGenerateSpeakerLink,
  } = usePanelistInviteModal();
  const isLimitReached = invitesRemaining !== null && invitesRemaining <= 0;

  return (
    <div className={styles.root}>
      <div className={styles.topActions}>
        <OButton text="Via QR Code" size="default" onClick={handleOpenQr} />
        <OButton text="Mail Invitation" size="default" onClick={handleOpenMail} />
      </div>

      <div className={styles.linkBlock}>
        <label className={styles.subTitle}>Link</label>
        <div className={styles.linkRow}>
          <div className={styles.linkText} title={meetingLink}>
            {meetingLink}
          </div>
          <OTooltip title="Copy" placement="top" isSmall>
            <IconButton id="invite-to-webinar-copy" onClick={() => handleCopyLink(meetingLink)}>
              <MdContentCopy className={styles.copyIcon} />
            </IconButton>
          </OTooltip>
        </div>
        {isHostUser && <div className={styles.divider} />}
      </div>

      {isHostUser && (
        <div className={styles.panelistsBlock}>
          <div className={cn(styles.subHeader, 'flex items-center justify-between')}>Invite Panelists</div>
          <div className={styles.panelRow}>
            <div className={styles.panelLabel}>
              Invite a co-host <span className={styles.hint}>(share with one person only)</span>
              {generatedCoHostLink && (
                <OTooltip title={isLimitReached ? "Limit Reached" : "Regenerate link"} placement="top" isSmall>
                  <OButton
                    className={styles.reGenBtn}
                    onClick={handleGenerateCoHostLink}
                    disabled={isGeneratingCoHost || isLimitReached}
                    icon={<MdAutorenew size={16} className={isGeneratingCoHost ? styles.rotating : ''} />}
                  />
                </OTooltip>
              )}
            </div>
            {generatedCoHostLink ? (
              <div className={styles.linkRow}>
                <div
                  className={cn(styles.linkText, { [styles.loadingText]: isGeneratingCoHost })}
                  title={isGeneratingCoHost ? 'Generating...' : generatedCoHostLink}
                >
                  {isGeneratingCoHost ? 'Generating new link...' : generatedCoHostLink}
                </div>
                <OTooltip title="Copy" placement="top" isSmall>
                  <IconButton onClick={() => handleCopyLink(generatedCoHostLink)} disabled={isGeneratingCoHost}>
                    <MdContentCopy className={styles.copyIcon} />
                  </IconButton>
                </OTooltip>
              </div>
            ) : (
              <OButton
                text="Generate link"
                size="full"
                outlined
                onClick={handleGenerateCoHostLink}
                loading={isGeneratingCoHost}
                isDisabled={isGeneratingCoHost || isLimitReached}
              />
            )}
          </div>
          <div className={styles.panelRow}>
            <div className={styles.panelLabel}>
              Invite a speaker <span className={styles.hint}>(share with one person only)</span>
              {generatedSpeakerLink && (
                <OTooltip title={isLimitReached ? "Limit Reached" : "Regenerate link"} placement="top" isSmall>
                  <OButton
                    className={styles.reGenBtn}
                    onClick={handleGenerateSpeakerLink}
                    disabled={isGeneratingSpeaker || isLimitReached}
                    icon={<MdAutorenew size={16} className={isGeneratingSpeaker ? styles.rotating : ''} />}
                  />
                </OTooltip>
              )}
            </div>
            {generatedSpeakerLink ? (
              <div className={styles.linkRow}>
                <div
                  className={cn(styles.linkText, { [styles.loadingText]: isGeneratingSpeaker })}
                  title={isGeneratingSpeaker ? 'Generating...' : generatedSpeakerLink}
                >
                  {isGeneratingSpeaker ? 'Generating new link...' : generatedSpeakerLink}
                </div>
                <OTooltip title="Copy" placement="top" isSmall>
                  <IconButton onClick={() => handleCopyLink(generatedSpeakerLink)} disabled={isGeneratingSpeaker}>
                    <MdContentCopy className={styles.copyIcon} />
                  </IconButton>
                </OTooltip>
              </div>
            ) : (
              <OButton
                text="Generate link"
                size="full"
                outlined
                onClick={handleGenerateSpeakerLink}
                loading={isGeneratingSpeaker}
                isDisabled={isGeneratingSpeaker || isLimitReached}
              />
            )}
          </div>
          {invitesRemaining !== null && invitesLimit !== null && (
            <div className={styles.invitesNote} style={{ color: invitesRemaining === 0 ? 'red' : undefined }}>
              {invitesRemaining === 0
                ? "You've reached the limit for generating panelist links"
                : `You have ${invitesRemaining} invites remaining out of ${invitesLimit}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo<IPanelistInviteContentProps>(PanelistInviteContent);
